import React, { useState, useEffect, useCallback } from "react";
import { Play, Pause, RotateCcw, Target, Navigation } from "lucide-react";

const GridPathAnalyzer = () => {
  const [gridSize, setGridSize] = useState({ rows: 5, cols: 5 });
  const [edges, setEdges] = useState(new Set());
  const [startNode, setStartNode] = useState(null);
  const [endNode, setEndNode] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [path, setPath] = useState([]);
  const [mode, setMode] = useState("move"); // 'edit', 'setStart', 'setEnd', 'move'
  const [pathCounts, setPathCounts] = useState(new Map());
  const [showPathCounts, setShowPathCounts] = useState(false);
  const [shortestPaths, setShortestPaths] = useState(new Map());
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // 获取节点ID
  const getNodeId = (row, col) => `${row}-${col}`;

  // 获取边ID
  const getEdgeId = (row1, col1, row2, col2) => {
    const [r1, c1, r2, c2] = [row1, col1, row2, col2].map((x) => parseInt(x));
    if (r1 === r2) {
      return `h-${r1}-${Math.min(c1, c2)}`;
    } else {
      return `v-${Math.min(r1, r2)}-${c1}`;
    }
  };

  // 初始边矩阵配置（可自定义修改）
  const initialEdgeMatrix = {
    // 水平边矩阵：每行表示一行的水平边，1表示有边，0表示无边
    // 对于5×5网格，每行有4条水平边
    horizontal: [
      [0, 0, 0, 1], // 第0行的水平边
      [0, 0, 1, 1], // 第1行的水平边
      [0, 1, 1, 1], // 第2行的水平边
      [1, 1, 1, 1], // 第3行的水平边
      [1, 1, 1, 1], // 第4行的水平边
    ],
    // 垂直边矩阵：每列表示一列的垂直边，1表示有边，0表示无边
    // 对于5×5网格，每列有4条垂直边
    vertical: [
      [0, 0, 0, 1], // 第0列的垂直边
      [0, 0, 1, 1], // 第1列的垂直边
      [0, 1, 1, 1], // 第2列的垂直边
      [1, 1, 1, 1], // 第3列的垂直边
      [1, 1, 1, 1], // 第4列的垂直边
    ],
  };

  // 根据当前网格大小调整边矩阵
  const getAdjustedEdgeMatrix = () => {
    const adjusted = {
      horizontal: [],
      vertical: [],
    };

    // 调整水平边矩阵
    for (let r = 0; r < gridSize.rows; r++) {
      adjusted.horizontal[r] = [];
      for (let c = 0; c < gridSize.cols - 1; c++) {
        // 如果原矩阵有这个位置的值就用，否则默认为0
        if (
          r < initialEdgeMatrix.horizontal.length &&
          c < initialEdgeMatrix.horizontal[r].length
        ) {
          adjusted.horizontal[r][c] = initialEdgeMatrix.horizontal[r][c];
        } else {
          adjusted.horizontal[r][c] = 0;
        }
      }
    }

    // 调整垂直边矩阵
    for (let c = 0; c < gridSize.cols; c++) {
      adjusted.vertical[c] = [];
      for (let r = 0; r < gridSize.rows - 1; r++) {
        // 如果原矩阵有这个位置的值就用，否则默认为0
        if (
          c < initialEdgeMatrix.vertical.length &&
          r < initialEdgeMatrix.vertical[c].length
        ) {
          adjusted.vertical[c][r] = initialEdgeMatrix.vertical[c][r];
        } else {
          adjusted.vertical[c][r] = 0;
        }
      }
    }

    return adjusted;
  };

  // 获取初始网格状态（基于矩阵配置）
  const getInitialEdges = () => {
    const initialEdges = new Set();
    const matrix = getAdjustedEdgeMatrix();

    // 添加水平边
    for (let r = 0; r < gridSize.rows; r++) {
      for (let c = 0; c < gridSize.cols - 1; c++) {
        if (matrix.horizontal[r] && matrix.horizontal[r][c] === 1) {
          initialEdges.add(getEdgeId(r, c, r, c + 1));
        }
      }
    }

    // 添加垂直边
    for (let c = 0; c < gridSize.cols; c++) {
      for (let r = 0; r < gridSize.rows - 1; r++) {
        if (matrix.vertical[c] && matrix.vertical[c][r] === 1) {
          initialEdges.add(getEdgeId(r, c, r + 1, c));
        }
      }
    }

    return initialEdges;
  };

  // 获取完整网格状态
  const getFullGrid = () => {
    const fullEdges = new Set();
    // 添加所有可能的边
    for (let r = 0; r < gridSize.rows; r++) {
      for (let c = 0; c < gridSize.cols; c++) {
        // 水平边
        if (c < gridSize.cols - 1) {
          fullEdges.add(getEdgeId(r, c, r, c + 1));
        }
        // 垂直边
        if (r < gridSize.rows - 1) {
          fullEdges.add(getEdgeId(r, c, r + 1, c));
        }
      }
    }
    return fullEdges;
  };

  // 获取相邻节点
  const getNeighbors = (nodeId) => {
    const [row, col] = nodeId.split("-").map(Number);
    const neighbors = [];

    // 检查四个方向
    const directions = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1], // 上、下、左、右
    ];

    directions.forEach(([dr, dc]) => {
      const newRow = row + dr;
      const newCol = col + dc;

      if (
        newRow >= 0 &&
        newRow < gridSize.rows &&
        newCol >= 0 &&
        newCol < gridSize.cols
      ) {
        const edgeId = getEdgeId(row, col, newRow, newCol);
        if (edges.has(edgeId)) {
          neighbors.push(getNodeId(newRow, newCol));
        }
      }
    });

    return neighbors;
  };

  // 检查节点是否有连接的边
  const hasConnections = (row, col) => {
    const nodeId = getNodeId(row, col);
    return getNeighbors(nodeId).length > 0;
  };

  // 计算最短路径距离和方向
  const findShortestPaths = useCallback(() => {
    if (!startNode || !endNode) return;

    const [startRow, startCol] = startNode.split("-").map(Number);
    const [endRow, endCol] = endNode.split("-").map(Number);

    // 使用BFS计算从起点到所有节点的实际最短距离
    const distances = new Map();
    const queue = [startNode];
    distances.set(startNode, 0);

    while (queue.length > 0) {
      const currentNodeId = queue.shift();
      const currentDistance = distances.get(currentNodeId);

      const neighbors = getNeighbors(currentNodeId);
      neighbors.forEach((neighbor) => {
        if (!distances.has(neighbor)) {
          distances.set(neighbor, currentDistance + 1);
          queue.push(neighbor);
        }
      });
    }

    // 构建最短路径的下一步节点映射
    const nextSteps = new Map();

    for (const [nodeId] of distances) {
      if (nodeId === endNode) continue;

      const [nodeRow, nodeCol] = nodeId.split("-").map(Number);
      const neighbors = getNeighbors(nodeId);
      const currentDistance = distances.get(nodeId);

      if (currentDistance === undefined) continue;

      // 找到距离终点更近的邻居节点
      const betterNeighbors = neighbors.filter((neighbor) => {
        const neighborDistance = distances.get(neighbor);
        return (
          neighborDistance !== undefined &&
          neighborDistance === currentDistance + 1
        );
      });

      if (betterNeighbors.length === 0) continue;

      // 计算当前节点到终点的方向需求
      const rowDiff = endRow - nodeRow; // 负数=需要向上，正数=需要向下，0=不需要行移动
      const colDiff = endCol - nodeCol; // 负数=需要向左，正数=需要向右，0=不需要列移动

      // 筛选出符合几何方向的移动
      const geometricallyCorrectMoves = betterNeighbors.filter((neighbor) => {
        const [neighborRow, neighborCol] = neighbor.split("-").map(Number);
        const moveRow = neighborRow - nodeRow; // 实际的行移动：-1=向上，1=向下
        const moveCol = neighborCol - nodeCol; // 实际的列移动：-1=向左，1=向右

        // 检查这个移动是否符合几何上的最短路径方向
        let isCorrectDirection = true;

        // 行方向检查：如果需要向某个方向移动，实际移动必须符合该方向
        if (rowDiff !== 0) {
          // 如果需要向上(rowDiff < 0)但实际向下(moveRow > 0)，或者
          // 如果需要向下(rowDiff > 0)但实际向上(moveRow < 0)，则不正确
          if (Math.sign(rowDiff) !== Math.sign(moveRow) && moveRow !== 0) {
            isCorrectDirection = false;
          }
        }

        // 列方向检查：如果需要向某个方向移动，实际移动必须符合该方向
        if (colDiff !== 0) {
          // 如果需要向左(colDiff < 0)但实际向右(moveCol > 0)，或者
          // 如果需要向右(colDiff > 0)但实际向左(moveCol < 0)，则不正确
          if (Math.sign(colDiff) !== Math.sign(moveCol) && moveCol !== 0) {
            isCorrectDirection = false;
          }
        }

        return isCorrectDirection;
      });

      // 如果有几何上正确的移动方向，优先使用；否则允许绕路（使用所有更近的邻居）
      nextSteps.set(
        nodeId,
        geometricallyCorrectMoves.length > 0
          ? geometricallyCorrectMoves
          : betterNeighbors
      );
    }

    setShortestPaths(nextSteps);
  }, [startNode, endNode, edges, getNeighbors]);

  // 初始化网格
  useEffect(() => {
    const newEdges = getInitialEdges();
    setEdges(newEdges);

    // 默认设置起点（左下角）和终点（右上角）
    const defaultStart = getNodeId(gridSize.rows - 1, 0);
    const defaultEnd = getNodeId(0, gridSize.cols - 1);
    setStartNode(defaultStart);
    setEndNode(defaultEnd);
    setCurrentNode(defaultStart);
    setPath([defaultStart]);
    setPathCounts(new Map());
    setShortestPaths(new Map());
    setErrorMessage("");
    setIsAutoPlaying(false);
  }, [gridSize]);

  // 更新最短路径计算
  useEffect(() => {
    findShortestPaths();
  }, [findShortestPaths]);

  // 生成完整网格
  const generateFullGrid = () => {
    const fullEdges = getFullGrid();
    setEdges(fullEdges);
    resetPath();
  };

  // 恢复初始状态
  const resetToInitial = () => {
    const initialEdges = getInitialEdges();
    setEdges(initialEdges);
    resetPath();
  };

  // 切换边的状态
  const toggleEdge = (edgeId) => {
    if (mode !== "edit") return;

    const newEdges = new Set(edges);
    if (newEdges.has(edgeId)) {
      newEdges.delete(edgeId);
    } else {
      newEdges.add(edgeId);
    }
    setEdges(newEdges);
  };

  // 设置节点
  const handleNodeClick = (row, col) => {
    const nodeId = getNodeId(row, col);

    if (mode === "setStart") {
      setStartNode(nodeId);
      setCurrentNode(nodeId);
      setPath([nodeId]);
      setMode("move");
    } else if (mode === "setEnd") {
      setEndNode(nodeId);
      setMode("move");
    }
  };

  // 手动移动
  const moveToNode = (targetNodeId) => {
    if (mode !== "move" || !currentNode) return;

    const neighbors = getNeighbors(currentNode);
    if (!neighbors.includes(targetNodeId)) return;

    // 检查是否在最短路径上
    const validNextSteps = shortestPaths.get(currentNode) || [];
    if (!validNextSteps.includes(targetNodeId)) {
      setErrorMessage("❌ 只能按最短路径方向移动！");
      setTimeout(() => setErrorMessage(""), 2000);
      return;
    }

    setCurrentNode(targetNodeId);
    setPath((prev) => [...prev, targetNodeId]);
    setErrorMessage("");

    // 如果到达终点，停止自动播放
    if (targetNodeId === endNode) {
      setIsAutoPlaying(false);
    }
  };

  // 自动移动
  const autoMove = useCallback(() => {
    if (!currentNode || currentNode === endNode || !isAutoPlaying) return;

    const validNextSteps = shortestPaths.get(currentNode) || [];
    if (validNextSteps.length > 0) {
      // 随机选择一个有效的下一步
      const randomIndex = Math.floor(Math.random() * validNextSteps.length);
      const nextNode = validNextSteps[randomIndex];

      setCurrentNode(nextNode);
      setPath((prev) => [...prev, nextNode]);

      if (nextNode === endNode) {
        setIsAutoPlaying(false);
      }
    } else {
      setIsAutoPlaying(false);
    }
  }, [currentNode, endNode, shortestPaths, isAutoPlaying]);

  // 自动播放定时器
  useEffect(() => {
    if (isAutoPlaying) {
      const timer = setInterval(autoMove, 1000);
      return () => clearInterval(timer);
    }
  }, [isAutoPlaying, autoMove]);

  // 开始/停止自动演示
  const toggleAutoPlay = () => {
    if (currentNode === endNode) {
      resetPath();
    }
    setIsAutoPlaying(!isAutoPlaying);
  };

  // 计算路径数（动态规划）
  const calculatePathCounts = useCallback(() => {
    if (!startNode) return;

    const counts = new Map();
    const distances = new Map();
    const queue = [startNode];

    counts.set(startNode, 1);
    distances.set(startNode, 0);

    // BFS 按层次计算
    while (queue.length > 0) {
      const currentNodeId = queue.shift();
      const currentDistance = distances.get(currentNodeId);
      const currentCount = counts.get(currentNodeId);

      const neighbors = getNeighbors(currentNodeId);
      neighbors.forEach((neighbor) => {
        if (!distances.has(neighbor)) {
          distances.set(neighbor, currentDistance + 1);
          counts.set(neighbor, 0);
          queue.push(neighbor);
        }

        // 如果邻居节点的距离比当前节点多1，则累加路径数
        if (distances.get(neighbor) === currentDistance + 1) {
          counts.set(neighbor, (counts.get(neighbor) || 0) + currentCount);
        }
      });
    }

    setPathCounts(counts);
  }, [startNode, getNeighbors]);

  // 重置路径
  const resetPath = () => {
    setCurrentNode(startNode);
    setPath(startNode ? [startNode] : []);
    setIsAutoPlaying(false);
    setErrorMessage("");
  };

  // 渲染网格
  const renderGrid = () => {
    const cellSize = 60;
    const strokeWidth = 4;
    const padding = 20; // 增加内边距

    // 计算SVG尺寸
    const svgWidth = gridSize.cols * cellSize;
    const svgHeight = gridSize.rows * cellSize;

    // 计算居中偏移
    const offsetX = padding + 10;
    const offsetY = padding + 10;

    return (
      <svg
        width={svgWidth}
        height={svgHeight}
        className="border border-gray-300 bg-white mx-auto"
      >
        <g transform={`translate(${offsetX}, ${offsetY})`}>
          {/* 渲染边 */}
          {Array.from(edges).map((edgeId) => {
            const parts = edgeId.split("-");
            let x1, y1, x2, y2;

            if (parts[0] === "h") {
              const row = parseInt(parts[1]);
              const col = parseInt(parts[2]);
              x1 = col * cellSize;
              y1 = row * cellSize;
              x2 = (col + 1) * cellSize;
              y2 = row * cellSize;
            } else {
              const row = parseInt(parts[1]);
              const col = parseInt(parts[2]);
              x1 = col * cellSize;
              y1 = row * cellSize;
              x2 = col * cellSize;
              y2 = (row + 1) * cellSize;
            }

            return (
              <line
                key={edgeId}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#4A90E2"
                strokeWidth={strokeWidth}
                className="cursor-pointer hover:stroke-blue-600"
                onClick={() => toggleEdge(edgeId)}
              />
            );
          })}

          {/* 渲染路径轨迹 */}
          {path.length > 1 &&
            path.map((nodeId, index) => {
              if (index === 0) return null;

              const [r1, c1] = path[index - 1].split("-").map(Number);
              const [r2, c2] = nodeId.split("-").map(Number);

              return (
                <line
                  key={`path-${index}`}
                  x1={c1 * cellSize}
                  y1={r1 * cellSize}
                  x2={c2 * cellSize}
                  y2={r2 * cellSize}
                  stroke="#FF6B6B"
                  strokeWidth={strokeWidth + 2}
                  opacity={0.8}
                />
              );
            })}

          {/* 渲染节点 */}
          {Array.from({ length: gridSize.rows }).map((_, row) =>
            Array.from({ length: gridSize.cols }).map((_, col) => {
              const nodeId = getNodeId(row, col);

              // 只渲染有连接的节点
              if (!hasConnections(row, col)) return null;

              const isStart = nodeId === startNode;
              const isCurrent = nodeId === currentNode;
              const isEnd = nodeId === endNode;
              const isInPath = path.includes(nodeId);

              let fillColor = "#E5E7EB";
              if (isStart) fillColor = "#10B981";
              else if (isEnd) fillColor = "#F59E0B";
              else if (isCurrent) fillColor = "#EF4444";
              else if (isInPath) fillColor = "#8B5CF6";

              const canMove =
                mode === "move" &&
                currentNode &&
                getNeighbors(currentNode).includes(nodeId) &&
                (shortestPaths.get(currentNode) || []).includes(nodeId);

              return (
                <g key={nodeId}>
                  <circle
                    cx={col * cellSize}
                    cy={row * cellSize}
                    r={12}
                    fill={fillColor}
                    stroke="#374151"
                    strokeWidth={2}
                    className={`cursor-pointer ${
                      canMove ? "hover:fill-red-300" : ""
                    }`}
                    onClick={() => {
                      if (mode === "move" && canMove) {
                        moveToNode(nodeId);
                      } else {
                        handleNodeClick(row, col);
                      }
                    }}
                  />
                  {showPathCounts && pathCounts.has(nodeId) && (
                    <text
                      x={col * cellSize}
                      y={row * cellSize + 3}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="12"
                      fill="#000000"
                      fontWeight="bold"
                      stroke="#000000"
                      strokeWidth="0.5"
                    >
                      {pathCounts.get(nodeId)}
                    </text>
                  )}
                </g>
              );
            })
          )}
        </g>
      </svg>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center">最短路径数</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 控制面板 */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">网格设置</h3>
            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-2">
                <label className="text-sm">行数</label>
                <input
                  type="number"
                  min="2"
                  max="8"
                  value={gridSize.rows}
                  onChange={(e) =>
                    setGridSize((prev) => ({
                      ...prev,
                      rows: parseInt(e.target.value),
                    }))
                  }
                  className="w-16 p-1 border rounded text-center"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm">列数</label>
                <input
                  type="number"
                  min="2"
                  max="8"
                  value={gridSize.cols}
                  onChange={(e) =>
                    setGridSize((prev) => ({
                      ...prev,
                      cols: parseInt(e.target.value),
                    }))
                  }
                  className="w-16 p-1 border rounded text-center"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={generateFullGrid}
                className="p-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
              >
                生成完整网格
              </button>
              <button
                onClick={resetToInitial}
                className="p-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
              >
                恢复初始状态
              </button>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">操作模式</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode("edit")}
                className={`p-2 rounded text-sm ${
                  mode === "edit" ? "bg-blue-500 text-white" : "bg-white border"
                }`}
              >
                编辑边
              </button>
              <button
                onClick={() => setMode("setStart")}
                className={`p-2 rounded text-sm ${
                  mode === "setStart"
                    ? "bg-green-500 text-white"
                    : "bg-white border"
                }`}
              >
                <Navigation className="w-4 h-4 inline mr-1" />
                设起点
              </button>
              <button
                onClick={() => setMode("setEnd")}
                className={`p-2 rounded text-sm ${
                  mode === "setEnd"
                    ? "bg-yellow-500 text-white"
                    : "bg-white border"
                }`}
              >
                <Target className="w-4 h-4 inline mr-1" />
                设终点
              </button>
              <button
                onClick={() => setMode("move")}
                disabled={!startNode}
                className={`p-2 rounded text-sm ${
                  mode === "move" ? "bg-red-500 text-white" : "bg-white border"
                } disabled:opacity-50`}
              >
                <Play className="w-4 h-4 inline mr-1" />
                移动
              </button>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">路径分析</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={calculatePathCounts}
                disabled={!startNode}
                className="w-full p-2 bg-purple-500 text-white rounded disabled:opacity-50"
              >
                计算路径数
              </button>
              <button
                onClick={() => setShowPathCounts(!showPathCounts)}
                className={`w-full p-2 rounded ${
                  showPathCounts
                    ? "bg-purple-600 text-white"
                    : "bg-white border"
                }`}
              >
                {showPathCounts ? "隐藏" : "显示"}路径数
              </button>
              <button
                onClick={resetPath}
                disabled={!startNode}
                className="w-full p-2 bg-gray-500 text-white rounded disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4 inline mr-1" />
                重置路径
              </button>
              <button
                onClick={toggleAutoPlay}
                disabled={!startNode || !endNode}
                className={`w-full p-2 rounded disabled:opacity-50 ${
                  isAutoPlaying
                    ? "bg-red-500 text-white"
                    : "bg-blue-500 text-white"
                }`}
              >
                {isAutoPlaying ? (
                  <>
                    <Pause className="w-4 h-4 inline mr-1" />
                    停止演示
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 inline mr-1" />
                    自动演示
                  </>
                )}
              </button>
            </div>
            {errorMessage && (
              <div className="mt-2 p-2 bg-red-100 text-red-700 text-sm rounded border border-red-300">
                {errorMessage}
              </div>
            )}
          </div>
        </div>

        {/* 网格显示 */}
        <div className="lg:col-span-2">
          <div className="bg-white p-4 rounded-lg border">
            <div className="overflow-auto flex justify-center">
              {renderGrid()}
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default GridPathAnalyzer;
